import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import { walletAPI } from '../services/walletService';

/**
 * Payment Provider Context
 * Manages multi-PSP (Payment Service Provider) routing
 * Routes Nigerian users to Flutterwave, others to Stripe
 */

export const PaymentProviderContext = createContext();

export const PaymentProviderProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [provider, setProvider] = useState('stripe'); // default
  const [isReady, setIsReady] = useState(false);
  const [userWalletData, setUserWalletData] = useState(null);

  // Fetch wallet status to get country/currency info
  useEffect(() => {
    const fetchUserWalletData = async () => {
      if (user?.userId) {
        try {
          const response = await walletAPI.getWalletStatus(user.userId);
          setUserWalletData(response.data.walletStatus);
        } catch (error) {
          console.error('Error fetching wallet data for provider detection:', error);
          setUserWalletData(null);
        }
      }
    };

    fetchUserWalletData();
  }, [user?.userId]);

  useEffect(() => {
    if (user) {
      // Determine provider based on wallet data (which has country/currency)
      const dataToCheck = userWalletData || user;
      const userProvider = shouldUseFlutterwave(dataToCheck) ? 'flutterwave' : 'stripe';
      setProvider(userProvider);
      setIsReady(true);
    } else {
      setProvider('stripe'); // default for non-authenticated users
      setIsReady(true);
    }
  }, [user, userWalletData]);

  /**
   * Determine if user should use Flutterwave
   */
  const shouldUseFlutterwave = (user) => {
    // Rule 1: Check currency code
    if (user.currencyCode && user.currencyCode.toUpperCase() === 'NGN') {
      return true;
    }

    // Rule 2: Check country
    if (user.country) {
      const country = user.country.toUpperCase();
      if (country === 'NG' || country === 'NIGERIA') {
        return true;
      }
    }

    // Rule 3: Check explicit preference
    if (user.payment_provider === 'flutterwave' || user.payment_provider === 'paystack') {
      return true;
    }

    return false;
  };

  const value = {
    provider,
    isReady,
    isFlutterwave: provider === 'flutterwave',
    isStripe: provider === 'stripe',
    shouldUseFlutterwave,
    // Backwards-compatible alias (deprecated)
    isPaystack: provider === 'flutterwave',
    shouldUsePaystack: shouldUseFlutterwave
  };

  return (
    <PaymentProviderContext.Provider value={value}>
      {children}
    </PaymentProviderContext.Provider>
  );
};

/**
 * Hook to use payment provider context
 */
export const usePaymentProvider = () => {
  const context = useContext(PaymentProviderContext);
  if (!context) {
    throw new Error('usePaymentProvider must be used within PaymentProviderProvider');
  }
  return context;
};
